import { defined, paymentAttemptKindSchema, } from '@teo/types';
import { err, invalidStateError, ok, transIdMismatchError, } from './errors.js';
export const VENDOR_EMAIL_MISSING_REASON = 'cannot send enrollment link — vendor has no email';
export function claimAttempt(strategy) {
    switch (strategy) {
        case 'payabli':
            return { kind: 'scheduling' };
        case 'autopayExternal':
            return { kind: 'awaitingExternalConfirmation' };
    }
}
export function claimForAuthorization(status) {
    if (status.kind !== 'scheduling' && status.kind !== 'awaitingVendorEnrollment') {
        return err(invalidStateError(status.kind, 'scheduling or awaitingVendorEnrollment', 'claim for authorization'));
    }
    if (status.kind === 'awaitingVendorEnrollment') {
        return ok({
            kind: 'authorizing',
            payabliBillId: status.payabliBillId,
            linkSentAt: status.linkSentAt,
        });
    }
    return ok({ kind: 'authorizing' });
}
export function parkForVendorEnrollment(status, input) {
    if (status.kind !== 'scheduling') {
        return err(invalidStateError(status.kind, 'scheduling', 'park for vendor enrollment'));
    }
    return ok({
        kind: 'awaitingVendorEnrollment',
        payabliBillId: input.payabliBillId,
        linkSentAt: input.linkSentAt,
    });
}
export function resumeFromVendorEnrollment(status) {
    if (status.kind !== 'awaitingVendorEnrollment') {
        return err(invalidStateError(status.kind, 'awaitingVendorEnrollment', 'resume from vendor enrollment'));
    }
    return ok({ kind: 'scheduling' });
}
export function confirmEnrollmentPayout(status, input) {
    if (status.kind !== 'awaitingVendorEnrollment') {
        return err(invalidStateError(status.kind, 'awaitingVendorEnrollment', 'confirm vendor enrollment payout'));
    }
    return ok({ kind: 'inTransit', payabliTransId: input.payabliTransId });
}
export function revertAuthorizationClaim(status) {
    if (status.kind !== 'authorizing') {
        return err(invalidStateError(status.kind, 'authorizing', 'revert authorization claim'));
    }
    if (status.payabliBillId !== undefined && status.linkSentAt !== undefined) {
        return ok({
            kind: 'awaitingVendorEnrollment',
            payabliBillId: status.payabliBillId,
            linkSentAt: status.linkSentAt,
        });
    }
    return ok({ kind: 'scheduling' });
}
export function dispatch(status, payabliTransId) {
    if (status.kind !== 'authorizing') {
        return err(invalidStateError(status.kind, 'authorizing', 'dispatch'));
    }
    return ok({
        kind: 'inTransit',
        payabliTransId,
        ...(status.payabliBillId === undefined ? {} : { payabliBillId: status.payabliBillId }),
    });
}
export function confirmPayabli(status, payabliTransId) {
    if (status.kind !== 'inTransit') {
        return err(invalidStateError(status.kind, 'inTransit', 'confirm payabli payment'));
    }
    if (payabliTransId !== status.payabliTransId) {
        return err(transIdMismatchError('confirm payabli payment'));
    }
    return ok({ kind: 'confirmed', payabliTransId });
}
export function confirmExternal(status) {
    if (status.kind !== 'awaitingExternalConfirmation') {
        return err(invalidStateError(status.kind, 'awaitingExternalConfirmation', 'confirm external payment'));
    }
    return ok({ kind: 'confirmed', payabliTransId: null });
}
export function failAttempt(status, reason) {
    if (status.kind !== 'scheduling' &&
        status.kind !== 'authorizing' &&
        status.kind !== 'inTransit' &&
        status.kind !== 'awaitingVendorEnrollment' &&
        status.kind !== 'awaitingExternalConfirmation') {
        return err(invalidStateError(status.kind, 'scheduling, authorizing, inTransit, awaitingVendorEnrollment, or awaitingExternalConfirmation', 'fail attempt'));
    }
    const payabliTransId = status.kind === 'inTransit' ? status.payabliTransId : null;
    return ok({ kind: 'failed', payabliTransId, reason });
}
export function withdrawAttempt(status) {
    if (status.kind !== 'scheduling' && status.kind !== 'awaitingExternalConfirmation') {
        return err(invalidStateError(status.kind, 'scheduling or awaitingExternalConfirmation', 'withdraw attempt'));
    }
    return ok({ kind: 'withdrawn' });
}
const PAYOUT_CAN_STILL_SEND = {
    scheduling: true,
    authorizing: true,
    awaitingVendorEnrollment: true,
    inTransit: true,
    awaitingExternalConfirmation: false,
    confirmed: false,
    failed: false,
    withdrawn: false,
    canceled: false,
};
export const PAYOUT_KINDS_THAT_CAN_STILL_SEND = paymentAttemptKindSchema.options.filter((kind) => PAYOUT_CAN_STILL_SEND[kind]);
export function cancelAttempt(status, cancel) {
    if (status.kind !== 'scheduling' &&
        status.kind !== 'authorizing' &&
        status.kind !== 'awaitingVendorEnrollment' &&
        status.kind !== 'inTransit') {
        return err(invalidStateError(status.kind, 'scheduling, authorizing, awaitingVendorEnrollment, or inTransit', 'cancel attempt'));
    }
    const payabliTransId = status.kind === 'inTransit' ? status.payabliTransId : (cancel.payabliTransId ?? null);
    const payabliBillId = status.kind === 'scheduling' ? undefined : status.payabliBillId;
    return ok({
        kind: 'canceled',
        payabliTransId,
        ...defined({ payabliBillId }),
        origin: cancel.origin,
    });
}
export function adoptRemoteAuthorization(status, payabliTransId) {
    if (status.kind !== 'scheduling' && status.kind !== 'authorizing') {
        return err(invalidStateError(status.kind, 'scheduling or authorizing', 'adopt a remote payout'));
    }
    if (payabliTransId.trim().length === 0) {
        return err(invalidStateError(status.kind, 'a non-empty Payabli TransId', 'adopt a remote payout'));
    }
    return ok({
        kind: 'inTransit',
        payabliTransId,
        ...(status.kind === 'authorizing' && status.payabliBillId !== undefined
            ? { payabliBillId: status.payabliBillId }
            : {}),
    });
}
export function canPostToLedger(status) {
    return status.kind === 'confirmed';
}
export function startLedgerPosting(status, ledgerPosting) {
    if (!canPostToLedger(status)) {
        return err(invalidStateError(status.kind, 'confirmed', 'start ledger posting'));
    }
    if (ledgerPosting.kind !== 'notStarted') {
        return err(invalidStateError(ledgerPosting.kind, 'notStarted', 'start ledger posting'));
    }
    return ok({ kind: 'posting' });
}
export function postToLedger(ledgerPosting, qboBillPaymentId) {
    if (ledgerPosting.kind !== 'posting') {
        return err(invalidStateError(ledgerPosting.kind, 'posting', 'post to ledger'));
    }
    return ok({ kind: 'posted', qboBillPaymentId });
}
export function failLedgerSync(ledgerPosting, qboError) {
    if (ledgerPosting.kind !== 'posting') {
        return err(invalidStateError(ledgerPosting.kind, 'posting', 'fail ledger sync'));
    }
    return ok({ kind: 'syncFailed', qboError });
}
export function retryLedgerSync(ledgerPosting) {
    if (ledgerPosting.kind !== 'syncFailed') {
        return err(invalidStateError(ledgerPosting.kind, 'syncFailed', 'retry ledger sync'));
    }
    return ok({ kind: 'notStarted' });
}
//# sourceMappingURL=paymentAttempt.js.map